import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { UserRole } from 'types/user.types';
import { RouteConfig } from 'config/routes';

export const usePermissions = () => {
  const { userRole } = useAuth();

  const permissions = useMemo(() => {
    if (!userRole) {
      return {
        canViewApplications: false,
        canCreateApplications: false,
        canEditApplications: false,
        canDeleteApplications: false,
        canViewPush: false,
        canSendPush: false,
        canManagePush: false,
        canCreateCampaigns: false,
        canEditCampaigns: false,
        canCreateSegments: false,
        canEditSegments: false,
        canViewStats: false,
        canExportData: false,
        canManageUsers: false,
        canGrantAccess: false,
        isAccManager: false,
        isViewer: false,
        isBuyer: false,
        canViewPushHistory: false,
      };
    }

    const isAdmin = userRole === UserRole.ADMIN;
    const isExecutor = userRole === UserRole.EXECUTOR;
    const isViewer = userRole === UserRole.VIEWER;
    const isAccManager = userRole === UserRole.ACC_MANAGER;
    const isBuyer = userRole === UserRole.BUYER;

    return {
      canViewApplications: isAdmin || isExecutor,
      canCreateApplications: isAdmin || isExecutor,
      canEditApplications: isAdmin || isExecutor,
      canDeleteApplications: isAdmin,
      canViewPush: isAdmin || isExecutor || isViewer,
      canViewPushHistory: isAdmin || isExecutor || isViewer || isAccManager || isBuyer,
      canSendPush: isAdmin || isExecutor,
      canCreateCampaigns: isAdmin || isExecutor,
      canEditCampaigns: isAdmin || isExecutor,
      canCreateSegments: isAdmin || isExecutor,
      canEditSegments: isAdmin || isExecutor,
      canManagePush: isAdmin || isExecutor,
      canViewStats: isAdmin || isExecutor,
      canExportData: isAdmin || isExecutor,
      canManageUsers: isAdmin,
      canGrantAccess: isAdmin,
      isAccManager,
      isViewer,
      isBuyer,
    };
  }, [userRole]);

  const canAccessRoute = useCallback(
    (route: RouteConfig): boolean => {
      if (!userRole) return false;
      if (!route.roles || route.roles.length === 0) return true;
      return route.roles.includes(userRole);
    },
    [userRole],
  );

  const filterRoutes = useCallback(
    (routes: RouteConfig[]): RouteConfig[] =>
      routes
        .filter(canAccessRoute)
        .map((route) =>
          route.children
            ? { ...route, children: filterRoutes(route.children) }
            : route,
        ),
    [canAccessRoute],
  );

  return {
    ...permissions,
    userRole,
    canAccessRoute,
    filterRoutes,
  };
};